import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Loader2, Plus, Ticket, Copy, Check, Ban, Gift, Download, Share2 } from "lucide-react";
import { formatCurrency } from "@/lib/currency";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/context/auth";
import type { GiftCardBrand } from "@/lib/gift-card-pdf";
import { createGiftCardImage } from "@/lib/gift-card-image";
import { ShareGiftCardDialog } from "@/components/share-gift-card-dialog";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

type Voucher = {
  id: number;
  code: string;
  value: number;
  balance: number;
  note: string | null;
  status: string;
  expiresAt: string | null;
  createdAt: string;
};

function loadImageData(url: string): Promise<string> {
  return fetch(url, { credentials: "include" }).then(async response => {
    if (!response.ok) throw new Error("Store logo could not be loaded");
    const blob = await response.blob();
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => typeof reader.result === "string" ? resolve(reader.result) : reject(new Error("Store logo could not be read"));
      reader.onerror = () => reject(new Error("Store logo could not be read"));
      reader.readAsDataURL(blob);
    });
  });
}

async function downloadGiftCard(voucher: Voucher, store: GiftCardBrand | null): Promise<boolean> {
  const logoUrl = store?.logoUrl
    ? `${window.location.origin}${BASE}/api/storage${store.logoUrl}`
    : null;
  const { file, logoIncluded } = await createGiftCardImage(voucher, store, logoUrl ? () => loadImageData(logoUrl) : undefined);
  const url = URL.createObjectURL(file);
  const link = document.createElement("a");
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Allow the browser to start the download before releasing the image.
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  return logoIncluded;
}

function statusBadge(v: Voucher) {
  if (v.status === "cancelled") return <Badge variant="destructive">Cancelled</Badge>;
  if (v.status === "used") return <Badge variant="secondary">Used</Badge>;
  if (v.expiresAt && new Date(v.expiresAt) < new Date()) return <Badge variant="secondary">Expired</Badge>;
  if (v.balance < v.value) return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Partial</Badge>;
  return <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Active</Badge>;
}

function CopyCode({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      className="flex items-center gap-1.5 font-mono text-sm font-semibold tracking-wider text-primary hover:opacity-80"
      onClick={() => { navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
      title="Copy code"
    >
      {code}
      {copied ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 opacity-50" />}
    </button>
  );
}

function CreateVoucherDialog({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const { store } = useAuth();
  const [value, setValue] = useState("");
  const [customCode, setCustomCode] = useState("");
  const [note, setNote] = useState("");
  const [expiresAt, setExpiresAt] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!value || Number(value) <= 0) { setError("Enter a valid voucher value"); return; }
    if (customCode.trim().length > 32) { setError("Custom voucher codes must be 32 characters or fewer"); return; }
    setLoading(true);
    setError("");
    try {
      const body: Record<string, unknown> = { value: Number(value) };
      if (customCode.trim()) body.code = customCode.trim();
      if (note.trim()) body.note = note.trim();
      if (expiresAt) body.expiresAt = expiresAt;

      const res = await fetch(`${BASE}/api/vouchers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to create voucher");
      onCreated();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="text-sm font-medium">Voucher Value (₦) <span className="text-destructive">*</span></label>
        <Input
          type="number"
          min="1"
          step="0.01"
          placeholder="e.g. 5000"
          value={value}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
          className="mt-1"
        />
      </div>
      <div>
        <label className="text-sm font-medium">Custom Code <span className="text-muted-foreground text-xs">(optional — auto-generated if blank)</span></label>
        <Input
          placeholder="e.g. XMAS-2025"
          maxLength={32}
          value={customCode}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomCode(e.target.value.toUpperCase())}
          className="mt-1 font-mono uppercase"
        />
      </div>
      <div>
        <label className="text-sm font-medium">Note <span className="text-muted-foreground text-xs">(optional)</span></label>
        <Input
          placeholder="e.g. Christmas gift for Amaka"
          value={note}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNote(e.target.value)}
          className="mt-1"
        />
      </div>
      <div>
        <label className="text-sm font-medium">Expiry Date <span className="text-muted-foreground text-xs">(optional)</span></label>
        <Input
          type="date"
          value={expiresAt}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setExpiresAt(e.target.value)}
          className="mt-1"
          min={new Date().toISOString().split("T")[0]}
        />
      </div>
      <div
        className="relative overflow-hidden rounded-xl border border-emerald-900/20 bg-gradient-to-br from-emerald-950 via-emerald-900 to-teal-800 p-4 text-white shadow-sm"
        data-testid="preview-gift-card"
      >
        <div className="flex items-center gap-3">
          {store?.logoUrl ? (
            <img
              src={`${BASE}/api/storage${store.logoUrl}`}
              alt={`${store.name} logo`}
              className="h-10 w-10 rounded-lg bg-white object-contain p-1"
              data-testid="img-gift-card-store-logo"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-white/15">
              <Gift className="h-5 w-5 text-amber-200" />
            </div>
          )}
          <div className="min-w-0">
            <p className="truncate text-sm font-bold uppercase tracking-wide" data-testid="text-gift-card-store">
              {store?.name || "Your Store"}
            </p>
            <p className="text-[10px] font-semibold tracking-[0.2em] text-amber-200">GIFT VOUCHER</p>
          </div>
        </div>
        <div className="mt-4 flex items-end justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-emerald-100/80">Voucher number</p>
            <p className="truncate font-mono text-sm font-bold tracking-wider" data-testid="text-gift-card-code">
              {customCode.trim() || "Assigned when created"}
            </p>
          </div>
          <p className="shrink-0 text-xl font-bold" data-testid="text-gift-card-value">
            {Number(value) > 0 ? formatCurrency(Number(value)) : formatCurrency(0)}
          </p>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">The card uses this store’s saved name and logo. The voucher number and value appear on the downloadable card.</p>
      {error && <p className="text-sm text-destructive bg-destructive/10 px-3 py-2 rounded-lg">{error}</p>}
      <div className="flex gap-2 pt-1">
        <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
        <Button className="flex-1 gap-2" onClick={handleCreate} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Gift className="w-4 h-4" />}
          Create Voucher
        </Button>
      </div>
    </div>
  );
}

export default function VouchersPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { store } = useAuth();
  const [createOpen, setCreateOpen] = useState(false);
  const [cancelling, setCancelling] = useState<number | null>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [sharingVoucher, setSharingVoucher] = useState<Voucher | null>(null);

  const { data: vouchers, isLoading } = useQuery<Voucher[]>({
    queryKey: ["vouchers"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/vouchers`, { credentials: "include" });
      if (!res.ok) throw new Error("Failed to load vouchers");
      return res.json();
    },
  });

  const handleCancel = async (id: number, code: string) => {
    if (!confirm(`Cancel voucher ${code}? This cannot be undone.`)) return;
    setCancelling(id);
    try {
      const res = await fetch(`${BASE}/api/vouchers/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status: "cancelled" }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? "Failed to cancel voucher");
      }
      queryClient.invalidateQueries({ queryKey: ["vouchers"] });
      toast({ title: `Voucher ${code} cancelled` });
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setCancelling(null);
    }
  };

  const handleDownloadCard = async (voucher: Voucher) => {
    setDownloadingId(voucher.id);
    try {
      const logoIncluded = await downloadGiftCard(voucher, store);
      toast({
        title: "Gift card image saved",
        description: store?.logoUrl && !logoIncluded
          ? "The card was saved without the store logo. Check that your saved logo is available, then try again."
          : `The PNG card for ${voucher.code} is ready to send on WhatsApp.`,
      });
    } catch (error) {
      toast({
        title: "Could not save gift card image",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const active = vouchers?.filter(v => v.status === "active" && (!v.expiresAt || new Date(v.expiresAt) >= new Date())) ?? [];
  const totalActiveValue = active.reduce((s, v) => s + v.balance, 0);

  return (
    <div className="space-y-6">
      {sharingVoucher && <ShareGiftCardDialog voucher={sharingVoucher} store={store} onClose={() => setSharingVoucher(null)} />}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Gift Vouchers</h1>
          <p className="text-muted-foreground mt-1">Create and manage gift voucher codes for your customers.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-2">
          <Plus className="w-4 h-4" /> Create Voucher
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div className="bg-muted/40 rounded-xl p-4 border border-border">
          <div className="text-xs text-muted-foreground mb-1">Total Vouchers</div>
          <div className="text-2xl font-bold">{vouchers?.length ?? 0}</div>
        </div>
        <div className="bg-muted/40 rounded-xl p-4 border border-border">
          <div className="text-xs text-muted-foreground mb-1">Active Vouchers</div>
          <div className="text-2xl font-bold text-emerald-600">{active.length}</div>
        </div>
        <div className="bg-muted/40 rounded-xl p-4 border border-border col-span-2 sm:col-span-1">
          <div className="text-xs text-muted-foreground mb-1">Outstanding Value</div>
          <div className="text-2xl font-bold text-primary">{formatCurrency(totalActiveValue)}</div>
        </div>
      </div>

      <div className="border rounded-md overflow-hidden">
        <div className="overflow-y-auto max-h-[600px]">
        <Table>
          <TableHeader className="sticky top-0 bg-background z-10">
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Remaining</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Expiry</TableHead>
              <TableHead>Note</TableHead>
              <TableHead className="w-[280px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow><TableCell colSpan={7} className="text-center py-8"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></TableCell></TableRow>
            ) : vouchers?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="py-16">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                      <Ticket className="w-6 h-6 text-muted-foreground/50" />
                    </div>
                    <div>
                      <p className="font-semibold">No vouchers yet</p>
                      <p className="text-sm text-muted-foreground mt-0.5">Create a gift voucher to give customers a credit they can spend at your store.</p>
                    </div>
                    <Button size="sm" className="gap-2 mt-1" onClick={() => setCreateOpen(true)}>
                      <Plus className="w-4 h-4" /> Create First Voucher
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : vouchers?.map(v => (
              <TableRow key={v.id}>
                <TableCell><CopyCode code={v.code} /></TableCell>
                <TableCell className="font-medium">{formatCurrency(v.value)}</TableCell>
                <TableCell className={v.balance < v.value ? "text-amber-600 font-medium" : ""}>
                  {formatCurrency(v.balance)}
                </TableCell>
                <TableCell>{statusBadge(v)}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {v.expiresAt ? new Date(v.expiresAt).toLocaleDateString("en-NG") : "No expiry"}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground max-w-[180px] truncate">{v.note || "—"}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1">
                    {v.status === "active" && (!v.expiresAt || new Date(v.expiresAt) >= new Date()) && v.balance > 0 && (
                      <Button variant="outline" size="sm" className="gap-2 whitespace-nowrap" title="Send gift card on WhatsApp" aria-label={`Send gift card ${v.code} on WhatsApp`} data-testid={`button-share-gift-card-${v.id}`} onClick={() => setSharingVoucher(v)}>
                        <Share2 className="w-4 h-4" /> Send to WhatsApp
                      </Button>
                    )}
                    {v.status === "active" && (!v.expiresAt || new Date(v.expiresAt) >= new Date()) && v.balance > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-2 whitespace-nowrap"
                        title="Save gift card as a PNG"
                        aria-label={`Save gift card ${v.code} as a PNG`}
                        data-testid={`button-download-gift-card-${v.id}`}
                        disabled={downloadingId === v.id}
                        onClick={() => handleDownloadCard(v)}
                      >
                        {downloadingId === v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        Save PNG
                      </Button>
                    )}
                    {v.status === "active" && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive"
                        title="Cancel voucher"
                        aria-label={`Cancel voucher ${v.code}`}
                        data-testid={`button-cancel-voucher-${v.id}`}
                        disabled={cancelling === v.id}
                        onClick={() => handleCancel(v.id, v.code)}
                      >
                        {cancelling === v.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Ban className="w-4 h-4" />}
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Gift className="w-5 h-5" /> Create Gift Voucher</DialogTitle>
          </DialogHeader>
          <CreateVoucherDialog
            onClose={() => setCreateOpen(false)}
            onCreated={() => {
              queryClient.invalidateQueries({ queryKey: ["vouchers"] });
              setCreateOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
